import { createContext, useCallback, useState } from 'react';
import { Bank, CountryOption } from '../../models/interfaces';
import topApi from '../../components/api/ToPApi';
import { errorMessage } from '../../utils/access';

type BanksContextProps = {
    banks: Bank[];
    countries: CountryOption[];
    isLoading: boolean;
    loadBanks: () => Promise<void>;
    saveBank: (bank: Partial<Bank>) => Promise<string>;   // crea o actualiza según tenga id
    deleteBank: (id: number) => Promise<string>;
}

export const BanksContext = createContext({} as BanksContextProps);

// Bancos (nivel plataforma). Un usuario de banco solo ve el suyo.
export const BanksProvider = ({ children }: any) => {

    const [banks, setBanks] = useState<Bank[]>([]);
    const [countries, setCountries] = useState<CountryOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadBanks = useCallback(async () => {
        setIsLoading(true);
        try {
            const resp = await topApi.get<{ banks: Bank[]; countries: CountryOption[] }>('/bank/getBanks');
            setBanks(resp.data.banks ?? []);
            setCountries(resp.data.countries ?? []);
        } catch {
            setBanks([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const saveBank = async (bank: Partial<Bank>) => {
        try {
            if (bank.id) {
                const resp = await topApi.put<{ bank: Bank }>(`/bank/${bank.id}`, { bank });
                setBanks(prev => prev.map(item => item.id === bank.id ? resp.data.bank : item));
            } else {
                const resp = await topApi.post<{ bank: Bank }>('/bank/', { bank });
                setBanks(prev => [...prev, resp.data.bank].sort((a, b) =>
                    (a.countryId ?? 0) - (b.countryId ?? 0) || a.name.localeCompare(b.name)));
            }
            return 'success';
        } catch (e) {
            return errorMessage(e);
        }
    }

    const deleteBank = async (id: number) => {
        try {
            const resp = await topApi.delete<{ bank: Bank }>(`/bank/${id}`);
            setBanks(prev => prev.map(item => item.id === id ? resp.data.bank : item));
            return 'success';
        } catch (e) {
            return errorMessage(e);
        }
    }

    return (
        <BanksContext.Provider value={{ banks, countries, isLoading, loadBanks, saveBank, deleteBank }}>
            {children}
        </BanksContext.Provider>
    );
}
